// Typed fetch wrapper. Reads rw_token cookie and sends as Bearer header.
// Redirects to auth on 401.

import { getToken } from './auth';
import type { Combinator, FilterRule } from './filters';

const AUTH_URL = 'https://auth.redwoodoperations.com';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    const redirect = encodeURIComponent(window.location.href);
    window.location.href = `${AUTH_URL}?redirect=${redirect}`;
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ── API types ──

export interface ViewSummary {
  id: number;
  name: string;
  accountCount: number;
  lastSyncedAt: string;
}

export interface AccountDetail {
  /** The CRM deal id. */
  id: string;
  /** The deal's page in the CRM. */
  crmUrl: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number | null;
  lng: number | null;
  fields: Record<string, unknown>;
}

export interface SyncStatus {
  status: 'idle' | 'running' | 'error';
  phase: string;
  progress: {
    totalAccounts: number;
    fetchedAccounts: number;
    geocodedAccounts: number;
    totalViews: number;
    processedViews: number;
  };
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

export interface PopupPreset {
  id: string;
  name: string;
  popupFields: string[];
  popupTitle: string;
}

export interface SavedFilter {
  id: string;
  name: string;
  rules: FilterRule[];
  combinator: Combinator;
  createdAt: string;
  updatedAt: string;
}

export type AutoZoomMode = 'on-view-change' | 'on-features-change';

export type PopupSize = 'small' | 'medium' | 'large';

export interface PopupConfig {
  presets: PopupPreset[];
  autoZoom: AutoZoomMode;
  clusterMarkers: boolean;
  popupSize: PopupSize;
}

export interface GeoJsonFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    id: string;
    name: string;
    address: string;
  };
}

export interface GeoJsonCollection {
  type: 'FeatureCollection';
  generatedAt: string;
  features: GeoJsonFeature[];
}

export interface AccountBlob {
  id: string;
  lat: number;
  lng: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  fields: Record<string, unknown>;
}

export interface AllAccountsDoc {
  generatedAt: string;
  accounts: AccountBlob[];
}

// ── API calls ──

export const api = {
  getViews: () => request<{ views: ViewSummary[] }>('GET', '/api/views'),
  getAccount: (id: string) => request<{ account: AccountDetail }>('GET', `/api/accounts/${encodeURIComponent(id)}`),
  triggerSync: () => request<{ started: boolean; message: string }>('POST', '/api/sync'),
  getSyncStatus: () => request<SyncStatus>('GET', '/api/sync/status'),
  getConfig: () => request<{ config: PopupConfig }>('GET', '/api/config'),
  updateConfig: (config: {
    presets: PopupPreset[];
    autoZoom: AutoZoomMode;
    clusterMarkers: boolean;
    popupSize: PopupSize;
  }) => request<{ config: PopupConfig }>('PUT', '/api/config', config),
  getFields: () => request<{ fields: string[]; labels: Record<string, string>; lastSyncedAt: string | null }>('GET', '/api/fields'),
  startFieldsSync: () => request<{ started: boolean }>('POST', '/api/fields/sync'),
  applyFilter: (rules: unknown[], combinator: 'AND' | 'OR') =>
    request<{ total: number; matched: number; features: GeoJsonFeature[] }>(
      'POST',
      '/api/filter',
      { rules, combinator },
    ),
  getFieldTypes: () =>
    request<{ types: Record<string, 'string' | 'number' | 'boolean' | 'date'> }>(
      'GET',
      '/api/field-types',
    ),
  listFilters: () => request<{ filters: SavedFilter[] }>('GET', '/api/filters'),
  saveFilter: (name: string, rules: FilterRule[], combinator: Combinator) =>
    request<{ filter: SavedFilter }>('POST', '/api/filters', { name, rules, combinator }),
  updateFilter: (
    id: string,
    patch: { name: string; rules: FilterRule[]; combinator: Combinator },
  ) => request<{ filter: SavedFilter }>('PUT', `/api/filters/${id}`, patch),
  deleteFilter: (id: string) =>
    request<{ deleted: boolean }>('DELETE', `/api/filters/${id}`),
  getFieldsSyncStatus: () =>
    request<{
      status: 'idle' | 'running' | 'error';
      scanned: number;
      total: number;
      startedAt: string | null;
      completedAt: string | null;
      lastSyncedAt: string | null;
      error: string | null;
    }>('GET', '/api/fields/sync/status'),
};

export async function fetchViewGeoJson(viewId: number): Promise<GeoJsonCollection> {
  const res = await fetch(`/data/views/${viewId}.json`);
  if (!res.ok) throw new Error(`Failed to load view data: ${res.status}`);
  return res.json() as Promise<GeoJsonCollection>;
}

export async function fetchAllAccounts(): Promise<AllAccountsDoc> {
  const res = await fetch('/data/accounts.json');
  if (!res.ok) throw new Error(`Failed to load accounts: ${res.status}`);
  return res.json() as Promise<AllAccountsDoc>;
}
